import {
  HappycoWorkOrderFragment,
  HappycoUserWorkOrdersQuery,
  HappycoUserWorkOrdersQueryVariables,
} from "gql/graphql";
import React, { memo, useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Select, SingleValue } from "chakra-react-select";
import { SystemStyleObject } from "@chakra-ui/react";
import colors from "app/colors";
import HAPPYCO_USER_WORK_ORDERS_QUERY from "./_networking/happyco-user-work-orders";
import HappycoWorkOrderOptionLabel from "./happyco-work-order-option-label";

interface IProps {
  onWorkOrderSelected: (
    happycoWorkOrder?: SingleValue<HappycoWorkOrderFragment>,
  ) => void;
}

const HappycoWorkOrderSelector = ({ onWorkOrderSelected }: IProps) => {
  const { data } = useQuery<
    HappycoUserWorkOrdersQuery,
    HappycoUserWorkOrdersQueryVariables
  >(HAPPYCO_USER_WORK_ORDERS_QUERY);

  const onChange = (
    happycoWorkOrder: SingleValue<HappycoWorkOrderFragment>,
  ) => {
    onWorkOrderSelected(happycoWorkOrder);
  };

  const getOptionLabel = useCallback(
    (happycoWorkOrder: HappycoWorkOrderFragment) =>
      happycoWorkOrder.description ?? "",
    [],
  );

  const getOptionValue = useCallback(
    (happycoWorkOrder: HappycoWorkOrderFragment) => happycoWorkOrder.happycoId,
    [],
  );

  const formatOptionLabel = useCallback(
    (
      option: HappycoWorkOrderFragment,
      { inputValue }: { inputValue: string },
    ) => (
      <HappycoWorkOrderOptionLabel
        happycoWorkOrder={option}
        search={inputValue}
      />
    ),
    [],
  );

  const filterOption = useCallback(
    (option: { data: HappycoWorkOrderFragment }, inputValue: string) =>
      option.data.description
        ?.toLowerCase()
        .includes(inputValue.toLowerCase()) ||
      option.data.property?.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.data.location?.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.data.category?.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.data.assigneeName
        ?.toLowerCase()
        .includes(inputValue.toLowerCase()) ||
      false,
    [],
  );

  const happycoUserWorkOrders = useMemo(
    () => data?.happycoUserWorkOrders || [],
    [data],
  );

  return (
    <Select
      options={happycoUserWorkOrders}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      formatOptionLabel={formatOptionLabel}
      filterOption={filterOption}
      placeholder="Select Work Order"
      onChange={onChange}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
          fontSize: 14,
        }),
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "full",
          borderColor: colors.grayPrimary,
        }),
        control: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          minHeight: "34px",
        }),
      }}
    />
  );
};

export default memo(HappycoWorkOrderSelector);
